import { Icon, IconButton, Menu, icons, theme, type MenuItem } from '@agentisai/design-system';
import type { CSSProperties, MouseEventHandler, Ref } from 'react';

export interface WorkstreamCardProps {
  readonly name: string;
  readonly description: string;
  readonly active?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Open the delete-confirm dialog. When omitted the menu has no Delete item. */
  readonly onDelete?: () => void;
  /** Open the rename dialog. When omitted the menu has no Rename item. */
  readonly onRename?: () => void;
  /** ref-as-prop (ADR-0014) → the card's selection `<button>`, so the rail can
   * scroll the selected card into view (S13). */
  readonly ref?: Ref<HTMLButtonElement>;
}

/**
 * Single workstream bubble in the Workstreams rail panel (S-C3).
 *
 * Lives in the app (not the design system) per frontend-architecture R0 —
 * it's a page-composition component, not a reusable DS primitive. Mirrors
 * the styling of the DS `RailPanel` Workstreams story (`accent.teal` active
 * tint), with an extra non-colour indicator (a thick left border) so the
 * active state is perceivable without colour (WCAG 1.4.1). Active card
 * carries `aria-current="true"`.
 *
 * The bubble is a container `<div>` (the visual frame + active tint) holding
 * two siblings: the selection `<button>` (name + description) and — when
 * `onRename`/`onDelete` is set — a "⋯" trigger opening a DS `Menu` with those
 * actions. Behind a menu rather than always-visible links so a stray click on
 * the card can't land on Delete. Keeping them siblings avoids a button nested
 * inside a button (invalid + a broken a11y target).
 */
export function WorkstreamCard({
  name,
  description,
  active,
  onClick,
  onDelete,
  onRename,
  ref,
}: WorkstreamCardProps) {
  const borderColor = active ? theme.color.accent.teal[500] : theme.color.neutral.border;
  // Non-colour active indicator: a 3px left border vs. 1px elsewhere makes the
  // active state visually distinct in a black-and-white render too (WCAG 1.4.1).
  // The container is the frame ONLY (no padding) — the selection button carries
  // the padding, so the whole padded area is the selection target rather than a
  // dead container ring. A row so the "⋯" trigger sits top-right beside it.
  const cardStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.space[2],
    background: active ? theme.color.accent.teal[50] : theme.color.neutral.white,
    border: `1px solid ${borderColor}`,
    borderLeftWidth: active ? '3px' : '1px',
    borderRadius: theme.radii.lg,
    fontFamily: theme.typography.fontFamily.sans,
  };
  // Inset the left padding by the extra 2px the active border adds so the text
  // doesn't shift between active / inactive.
  const padLeft = active ? `calc(${theme.space[3]} - 2px)` : theme.space[3];
  const hasActions = Boolean(onRename || onDelete);
  // The selection target fills the rest of the card and owns the padding + pointer.
  const selectStyle: CSSProperties = {
    display: 'block',
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    background: 'transparent',
    border: 0,
    paddingTop: theme.space[3],
    // The trigger brings its own inset — don't double the gutter beside it.
    paddingRight: hasActions ? 0 : theme.space[3],
    paddingBottom: theme.space[3],
    paddingLeft: padLeft,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily.sans,
  };
  return (
    <div style={cardStyle}>
      <button
        ref={ref}
        type="button"
        aria-current={active ? 'true' : undefined}
        onClick={onClick}
        style={selectStyle}
      >
        <div
          style={{
            fontSize: theme.typography.fontSize.bodySm,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.color.neutral.body,
            marginBottom: theme.space[1],
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: theme.typography.fontSize.tiny,
            fontWeight: theme.typography.fontWeight.regular,
            color: theme.color.neutral.faint,
            lineHeight: theme.typography.lineHeight.body,
          }}
        >
          {description}
        </div>
      </button>
      {hasActions && (
        <div style={{ padding: theme.space[2] }}>
          <Menu
            aria-label={`Actions for ${name}`}
            placement="bottom-end"
            minWidth={160}
            trigger={
              <IconButton
                aria-label={`Actions for ${name}`}
                tooltip="More actions"
                icon={<Icon as={icons.MoreHorizontal} size={14} />}
                size="sm"
              />
            }
            items={[
              ...(onRename
                ? [
                    {
                      id: 'rename',
                      label: 'Rename',
                      icon: <Icon as={icons.Edit} size={16} />,
                      onSelect: onRename,
                    } satisfies MenuItem,
                  ]
                : []),
              ...(onDelete
                ? [
                    {
                      id: 'delete',
                      label: 'Delete',
                      icon: <Icon as={icons.Trash2} size={16} />,
                      tone: 'danger',
                      onSelect: onDelete,
                    } satisfies MenuItem,
                  ]
                : []),
            ]}
          />
        </div>
      )}
    </div>
  );
}
WorkstreamCard.displayName = 'WorkstreamCard';
